/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router-dom";
import { push } from "react-router-redux";
import Yup from "yup";
import { bool, func, shape, string } from "prop-types";

import { Login } from "@ui-kit/components/index.es";

import Captcha from "pages/_components/fields/credentials/Captcha";
import withExchangeToken from "pages/_components/withExchangeToken";
import { resizableRoute } from "pages/_components/Resizable";

import { actions as layoutActions } from "reducers/layout";
import { actions as loginActions, selectors as loginSelectors, types as loginTypes } from "reducers/login";
import { actions as notificationActions } from "reducers/notification";
import { actions as recoveryPasswordActions, selectors as recoveryPasswordSelectors } from "reducers/recoveryPassword";
import { actions as statusActions } from "reducers/status";
import { selectors as sessionSelectors } from "reducers/session";

import * as i18n from "util/i18n";
import * as configUtil from "util/config";
import openExternalUrl from "util/openExternalUrl";

import { selectors as configSelectors } from "reducers/config";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { getPostLoginHomePath, getUnauthenticatedLoginPath } from "util/v3UxConfig";

import { LOGIN_LABELS_KEYS, LOGIN_TEXT_KEYS } from "./loginI18nKeys";
import { formatLoginStep2FirstName, getLoginStep2WelcomeText } from "./loginStep2Welcome";

function LoginStep2V3({
    dispatch,
    location,
    userFirstName,
    username,
    exchangeToken,
    activeEnvironment,
    showCaptcha,
    resetCaptcha,
    isSubmitting,
    recoveryAsyncPending,
    securitySeal,
    fieldErrors,
    rememberedUser,
    userpassNotificationShowed,
    uxLoginImprovements,
    uxDashboardImprovements,
}) {
    const securitySealSrc = typeof securitySeal === "number" ? String(securitySeal) : securitySeal || "";
    const hasRememberedDocument = Boolean(rememberedUser?.document && rememberedUser?.documentType);
    const hasRememberedUsername = Boolean(rememberedUser?.username);
    const formattedFirstName = useMemo(() => formatLoginStep2FirstName(userFirstName), [userFirstName]);
    const showSecuritySealTitle = hasRememberedDocument && hasRememberedUsername && Boolean(formattedFirstName);
    const welcomeText = useMemo(() => getLoginStep2WelcomeText(userFirstName, showSecuritySealTitle), [
        userFirstName,
        showSecuritySealTitle,
    ]);
    const securitySealTitle = i18n.get("login.step2.header");
    useEffect(() => {
        dispatch(layoutActions.setScopes(["loginStep2"]));
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(loginActions.resetLoading());
        };
    }, [dispatch]);

    useEffect(() => {
        const pathname = location?.pathname || "";
        const onStep2Path =
            pathname === "/v3/login/step2" ||
            pathname === "/v3/login/step2/" ||
            pathname === "/loginStep2" ||
            pathname === "/loginStep2/";

        if (activeEnvironment && onStep2Path) {
            dispatch(push(getPostLoginHomePath(uxDashboardImprovements)));
        } else if (!username && onStep2Path) {
            dispatch(push(getUnauthenticatedLoginPath(uxLoginImprovements)));
        }
        dispatch(statusActions.saveLastHref({ location }));
    }, [dispatch, username, activeEnvironment, location, uxLoginImprovements, uxDashboardImprovements]);

    useEffect(() => {
        const showUserpassWarning = Boolean(location?.state?.showUserpassWarning);
        const userpassWarningMessage = location?.state?.userpassWarningMessage;

        if (!showUserpassWarning || userpassNotificationShowed || !userpassWarningMessage) {
            return;
        }

        dispatch(
            notificationActions.showNotification(
                userpassWarningMessage,
                "warning",
                ["loginStep2"],
                i18n.get("enrollment.stepIRS.warning.title"),
            ),
        );
        dispatch({ type: loginTypes.USERPASS_NOTIFICATION_SHOWED });
    }, [dispatch, location, userpassNotificationShowed]);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                password: Yup.string().required(i18n.get("login.step2.password.required")),
                captcha: showCaptcha ? Yup.string().required(i18n.get("login.step2.captcha.required")) : Yup.string(),
            }),
        [showCaptcha],
    );

    const handleSubmit = useCallback(
        (values, formikBag) => {
            const password = values.password ?? "";
            const captcha = values.captcha ?? "";
            dispatch(loginActions.setResetCaptcha(false));
            if (
                configUtil.get("core.sessionHandler.componentFQN") ===
                "com.technisys.omnichannel.core.session.DbSessionHandler"
            ) {
                dispatch(loginActions.loginStep2(password, captcha, formikBag));
            } else {
                dispatch(loginActions.oauth(password, captcha, formikBag, showCaptcha));
            }
        },
        [dispatch, showCaptcha],
    );

    const handleRecoverPassword = useCallback(() => {
        dispatch(recoveryPasswordActions.recoveryPassPreview(username, exchangeToken));
    }, [dispatch, username, exchangeToken]);

    const handleChangeLoggedUser = useCallback(() => {
        dispatch(loginActions.removeRememberedUser());
        dispatch(loginActions.reset());
        dispatch(push(getUnauthenticatedLoginPath(uxLoginImprovements)));
    }, [dispatch, uxLoginImprovements]);

    const handleSimulate = useCallback(() => {
        const url = configUtil.get("plazoFijoWeb.URL.PaginaCMF");
        openExternalUrl(url);
    }, []);

    const enableSimulate =
        configUtil.getBoolean("plazoFijoWeb.Funcion.Activa") &&
        configUtil.getBoolean("frontend.show.plazoFijoWeb") &&
        !!hasRememberedDocument &&
        !!hasRememberedUsername;

    const getLabels = useCallback(
        (id) => {
            if (id === "simulate" && enableSimulate) {
                const configLabel = configUtil.get("plazoFijoWeb.Boton.Nombre");
                if (configLabel && configLabel.toLowerCase() !== "plazo fijo web") {
                    return configLabel;
                }
                return i18n.get("login.step1.simulate.label");
            }
            return i18n.get(LOGIN_LABELS_KEYS[id] ?? id);
        },
        [enableSimulate],
    );
    const getText = useCallback((id) => i18n.get(LOGIN_TEXT_KEYS[id] ?? id), []);

    const additionalInputs = useMemo(() => {
        if (!showCaptcha) {
            return [];
        }
        return [
            {
                id: "captcha",
                colProps: { size: { xs: 12 } },
                isField: true,
                customElement: (fieldProps) => <Captcha {...fieldProps} resetCaptcha={resetCaptcha} />,
            },
        ];
    }, [showCaptcha, resetCaptcha]);

    return (
        <>
            <Login
                key="loginStep2"
                initialValues={{
                    document: { number: "", type: { value: "DNI", label: "DNI" } },
                    user: "",
                    rememberDocumentUser: false,
                    password: "",
                    captcha: "",
                }}
                validationSchema={validationSchema}
                handleSubmit={handleSubmit}
                handleRecoverUser={handleRecoverPassword}
                handleSimulate={handleSimulate}
                getLabels={getLabels}
                getText={getText}
                step={2}
                hasUser
                welcomeText={welcomeText}
                securitySeal={securitySealSrc}
                enableChangeUser={hasRememberedDocument && hasRememberedUsername}
                handleChangeLoggedUser={handleChangeLoggedUser}
                enableSimulate={enableSimulate}
                showSimulate={enableSimulate}
                additionalInputs={additionalInputs}
                submitLoading={isSubmitting}
                recoverActionLoading={recoveryAsyncPending}
                showSecuritySealTitle={showSecuritySealTitle}
                securitySealTitle={securitySealTitle}
                fieldErrors={fieldErrors}
            />
        </>
    );
}

LoginStep2V3.propTypes = {
    dispatch: func.isRequired,
    location: shape({}).isRequired,
    userFirstName: string.isRequired,
    username: string.isRequired,
    exchangeToken: string.isRequired,
    activeEnvironment: shape({}),
    securitySeal: string,
    showCaptcha: bool.isRequired,
    resetCaptcha: bool.isRequired,
    isSubmitting: bool.isRequired,
    recoveryAsyncPending: bool,
    fieldErrors: shape({}),
    rememberedUser: shape({
        document: string,
        documentType: string,
        username: string,
    }),
    userpassNotificationShowed: bool.isRequired,
    uxLoginImprovements: bool,
    uxDashboardImprovements: bool,
};

LoginStep2V3.defaultProps = {
    activeEnvironment: null,
    securitySeal: "",
    fieldErrors: null,
    rememberedUser: null,
    recoveryAsyncPending: false,
    uxLoginImprovements: false,
    uxDashboardImprovements: false,
};

const mapStateToProps = (state) => ({
    userFirstName: loginSelectors.getUserFirstName(state) || "",
    username: loginSelectors.getUsername(state),
    exchangeToken: loginSelectors.getExchangeToken(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    showCaptcha: loginSelectors.getShowCaptcha(state),
    resetCaptcha: loginSelectors.getResetCaptcha(state),
    isSubmitting: loginSelectors.getFetching(state),
    recoveryAsyncPending: recoveryPasswordSelectors.getAsyncPending(state),
    securitySeal: loginSelectors.getSecuritySeal(state),
    fieldErrors: loginSelectors.getFieldErrors(state),
    rememberedUser: loginSelectors.getRememberedUser(state),
    userpassNotificationShowed: loginSelectors.getUserpassNotificationShowed(state),
    uxLoginImprovements: !!configSelectors.getConfigKey(state, "frontend.UXImprovements.Login.functionalities"),
    uxDashboardImprovements: isUxDashboardImprovementsEnabled(),
});

export default compose(withRouter, connect(mapStateToProps), resizableRoute, withExchangeToken)(LoginStep2V3);
